import type { EditDraft, SystemMode, TypedArray } from '@mapmaker/core';
import type { EditState, MeshData, PortLike, Request, Response, RunSummary, StaleStage, SystemInfo, WorldStats } from './protocol';

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void; onProgress?: (p: { stageId: string; fraction: number; message?: string }) => void; onCheckpoint?: (b: Uint8Array) => void };

/** Main-thread side of the engine protocol. */
export class EngineClient {
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();

  constructor(private readonly port: PortLike) {
    port.onmessage = (e) => {
      const m = e.data as Response;
      const p = this.pending.get(m.id);
      if (!p) return;
      if (m.op === 'progress') p.onProgress?.({ stageId: m.stageId, fraction: m.fraction, ...(m.message !== undefined ? { message: m.message } : {}) });
      else if (m.op === 'checkpoint') p.onCheckpoint?.(m.bytes);
      else {
        this.pending.delete(m.id);
        if (m.op === 'error') p.reject(new Error(m.message)); else p.resolve(m.value);
      }
    };
  }

  private call<T>(req: (id: number) => Request, extra: Partial<Pending> = {}, transfer?: Transferable[]): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, ...extra });
      this.port.postMessage(req(id), transfer);
    });
  }

  /** Transfers a copy, so the caller's buffer stays usable. */
  load(bytes: Uint8Array): Promise<{ viewOnly: boolean; staleStages: string[]; tick: number }> {
    const copy = bytes.slice();
    return this.call((id) => ({ id, op: 'load', bytes: copy }), {}, [copy.buffer as ArrayBuffer]);
  }

  run(opts: { variant?: string; only?: string[]; from?: string; checkpointEvery?: number; strict?: boolean; onProgress?: Pending['onProgress']; onCheckpoint?: Pending['onCheckpoint'] } = {}): Promise<RunSummary> {
    const { onProgress, onCheckpoint, ...rest } = opts;
    return this.call((id) => ({ id, op: 'run', ...rest }), { ...(onProgress ? { onProgress } : {}), ...(onCheckpoint ? { onCheckpoint } : {}) });
  }

  appendEdits(edits: EditDraft[], now = ''): Promise<EditState> { return this.call((id) => ({ id, op: 'edit', action: 'append', edits, now })); }
  editAction(action: 'undo' | 'redo' | 'clear' | 'state'): Promise<EditState> { return this.call((id) => ({ id, op: 'edit', action })); }
  /** Sets the per-stage switches (entries replace the current ones) when given; returns the stages and their switches. */
  systems(set?: Record<string, SystemMode>): Promise<SystemInfo[]> { return this.call((id) => ({ id, op: 'systems', ...(set ? { set } : {}) })); }
  stale(variant?: string): Promise<StaleStage[]> { return this.call((id) => ({ id, op: 'stale', ...(variant ? { variant } : {}) })); }
  create(params: unknown): Promise<{ viewOnly: boolean }> { return this.call((id) => ({ id, op: 'create', params })); }
  layers(ids: string[], mesh?: string): Promise<Record<string, TypedArray[]>> { return this.call((id) => ({ id, op: 'layers', ids, ...(mesh ? { mesh } : {}) })); }
  meshData(mesh?: string): Promise<MeshData> { return this.call((id) => ({ id, op: 'meshdata', ...(mesh ? { mesh } : {}) })); }
  stats(): Promise<WorldStats> { return this.call((id) => ({ id, op: 'stats' })); }

  cancel(): void { this.port.postMessage({ op: 'cancel' } satisfies Request); }
  snapshot(): Promise<Uint8Array> { return this.call((id) => ({ id, op: 'snapshot' })); }
  hash(): Promise<string> { return this.call((id) => ({ id, op: 'hash' })); }
  kernel<T>(name: string, args: unknown): Promise<T> { return this.call((id) => ({ id, op: 'kernel', name, args })); }
  worldKernel<T>(name: string, args: unknown): Promise<T> { return this.call((id) => ({ id, op: 'worldKernel', name, args })); }
}
